import { inject, Injectable, TemplateRef } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogData } from 'src/app/api/model/dialogData';
import { AppResource } from 'src/app/app.resource';
import { VyvEmptyPopupComponent } from 'src/app/shared/components/vyv-empty-popup/vyv-empty-popup.component';
import {
  VyvPopupComponent,
  VyvPopupConfig,
} from 'src/app/shared/components/vyv-popup/vyv-popup.component';
import { environment } from 'src/environments/environment';

export interface VyvCustomDialogConfig {
  title: string;
  template: TemplateRef<void>;
  actionText?: string;
  onValidate?: () => void;
  noDefaultAction?: boolean; /** Si true, n'affiche aucun bouton par défaut (gérer les actions dans le template) */
}

@Injectable({ providedIn: 'root' })
export class VyvDialogService {
  resource = inject(AppResource)['resource'];
  private loadingRef: DynamicDialogRef | undefined;
  private loadingTimeoutId: ReturnType<typeof setTimeout> | undefined;
  constructor(private dialogService: DialogService) {}

  /**
   * Ouvre une modale personnalisée avec le style VYV
   * @param config Configuration de la modale avec template personnalisé
   * @returns Référence à la modale ouverte
   */
  openCustomDialog(config: VyvCustomDialogConfig): DynamicDialogRef {
    const dialogData: DialogData = {
      isCustom: true,
      title: config.title,
      showTitle: true,
      contentTemplate: config.template,
      actions: config.noDefaultAction
        ? []
        : [
            {
              type: 'confirm',
              text: config.actionText || 'Continuer',
            },
          ],
    };

    const ref = this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: dialogData,
    });

    if (config.onValidate) {
      ref.onClose.subscribe((result) => {
        if (result && config.onValidate) {
          config.onValidate();
        }
      });
    }

    return ref;
  }

  /**
   * Ouvre une modale de message simple avec le style VYV
   * @param title Titre de la modale
   * @param message Message à afficher
   * @param isError Indique si le message est une erreur
   * @returns Référence à la modale ouverte
   */
  openErrorDialogApi(): DynamicDialogRef {
    const dialogData: DialogData = {
      isCustom: true,
      title: this.resource.error.oups,
      showTitle: true,
      content: [this.resource.error.api],
      actions: [
        {
          type: 'confirm',
          text: 'OK',
        },
      ],
    };

    return this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: dialogData,
    });
  }

  openClimaticRiskDialog(content: string): DynamicDialogRef {
    const dialogData: DialogData = {
      isCustom: false,
      title: '',
      showTitle: false,
      content: [content],
      actions: [],
    };
    return this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: dialogData,
    });
  }

  openLoadingDialog(isLoading: boolean, message: string): DynamicDialogRef {
    let lottie: string = `assets/${environment.brand}/lotties/edition_document.json`;
    if (isLoading) {
      lottie = `assets/${environment.brand}/lotties/loading.json`;
    }

    const messageContent = message ?? this.resource.quote_confirmation.quotationCreation;
    return this.dialogService.open(VyvEmptyPopupComponent, {
      ...VyvPopupConfig,
      data: {
        lottiePath: lottie,
        content: [messageContent],
      } as Partial<DialogData>,
    });
  }
}
